import { Box, Button, Heading, NavArrowRight, TabMenu, TabMenuItem, TextBlock } from "@bcc-ui/ui-components";
import classNames from "classnames";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import style from "./ai-banner.scss";
import { DispositionIcon } from "./icons/DispositionIcon";
import { RiskIcon } from "./icons/RiskIcon";
import { ValueIcon } from "./icons/ValueIcon";
import AIDisclaimer from "components/ai-panel/ai-disclaimer/AIDisclaimer";
import { AIQueryInput } from "components/ai-panel/ai-query-input/AIQueryInput";
import { useAIPanelContext } from "components/ai-panel/AIPanelContext";

import backgroundImage from "assets/images/bam-ai-banner/ai_banner_background.jpg";

import testIds from "testIds.json";

enum TagGroup {
    RISK = "RISK",
    DISPOSITION = "DISPOSITION",
    VALUE = "VALUE",
}

interface Tag {
    text: string;
    group: TagGroup;
}

interface Props {
    assetsTotalCount: number;
}

export const AiBanner = (props: Props) => {
    const { t } = useTranslation();

    const tabItems: { label: string; icon: React.ReactNode; group: TagGroup }[] = [
        {
            label: "AssetManagement.overview.aiBanner.riskTab.risk",
            icon: <RiskIcon />,
            group: TagGroup.RISK,
        },
        {
            label: "AssetManagement.overview.aiBanner.dispositionTab.disposition",
            icon: <DispositionIcon />,
            group: TagGroup.DISPOSITION,
        },
        {
            label: "AssetManagement.overview.aiBanner.valueTab.value",
            icon: <ValueIcon />,
            group: TagGroup.VALUE,
        },
    ];

    const tags: Tag[] = [
        {
            text: "AssetManagement.overview.aiBanner.riskTab.highRiskDevices",
            group: TagGroup.RISK,
        },
        {
            text: "AssetManagement.overview.aiBanner.riskTab.lockedDevices",
            group: TagGroup.RISK,
        },
        {
            text: "AssetManagement.overview.aiBanner.dispositionTab.failedGrading",
            group: TagGroup.DISPOSITION,
        },
        {
            text: "AssetManagement.overview.aiBanner.dispositionTab.resaleValue",
            group: TagGroup.DISPOSITION,
        },
        {
            text: "AssetManagement.overview.aiBanner.dispositionTab.dispositionPlan",
            group: TagGroup.DISPOSITION,
        },
        {
            text: "AssetManagement.overview.aiBanner.valueTab.totalRecoveryValue",
            group: TagGroup.VALUE,
        },
        {
            text: "AssetManagement.overview.aiBanner.valueTab.maximiseRecoveryValue",
            group: TagGroup.VALUE,
        },
        {
            text: "AssetManagement.overview.aiBanner.valueTab.gradingResults",
            group: TagGroup.VALUE,
        },
    ];

    const [activeGroup, setActiveGroup] = useState<TagGroup>(TagGroup.DISPOSITION);

    const visibleTags = tags.filter((tag) => tag.group === activeGroup);

    const { isAiPanelOpen, setIsAiPanelOpen, setPendingPrompt, isProcessingPrompt } = useAIPanelContext();

    return (
        <Box
            columnLayout
            horizontalAlignment="Center"
            verticalAlignment="Center"
            style={{ backgroundImage: `url(${backgroundImage})` }}
            className={style.banner}
            testId={testIds.workArea.asset.customView.overview.aiBanner.itself}
        >
            <Box columnLayout horizontalAlignment="Center" verticalAlignment="Center" gap={"Xxs"} paddingBottom="Md">
                <Heading variant="h1" className={style.heading}>
                    {t("AssetManagement.overview.aiBanner.heading")}
                </Heading>
                <TextBlock alignCenter disableBottomSpacing className={style.subHeading}>
                    {t("AssetManagement.overview.aiBanner.description", { count: props.assetsTotalCount })}
                </TextBlock>
            </Box>

            <Box
                horizontalAlignment="Center"
                verticalAlignment="Center"
                paddingBottom="Lg"
                className={style.inputFieldContainer}
            >
                <AIQueryInput
                    onSubmit={(inputText) => {
                        setIsAiPanelOpen(true);
                        setPendingPrompt(inputText);
                    }}
                    disabled={isAiPanelOpen || isProcessingPrompt}
                    placeholder={t("AssetManagement.overview.aiBanner.inputFieldPlaceholder")}
                    customTrigger={
                        <Box paddingRight={"Xxs"}>
                            <Button
                                type="submit"
                                variant="Primary"
                                small
                                className={
                                    isAiPanelOpen || isProcessingPrompt
                                        ? style.disabledInputFieldButton
                                        : style.inputFieldButton
                                }
                                testId={testIds.workArea.asset.customView.overview.aiBanner.submitButton}
                            >
                                {t("AssetManagement.overview.aiBanner.inputFieldButtonText")}
                            </Button>
                        </Box>
                    }
                    className={style.inputField}
                    queryInputTestId={testIds.workArea.asset.customView.overview.aiBanner.queryInput}
                    submitButtonTestId={testIds.workArea.asset.customView.overview.aiBanner.submitButton}
                    hideDisclaimer
                />
            </Box>

            <TabMenu className={style.tabMenu} dataType="AI_QUERY_SUGGETIONS">
                {tabItems.map((item, index) => (
                    <TabMenuItem
                        key={index}
                        isActive={activeGroup === item.group}
                        onClick={() => setActiveGroup(item.group)}
                        className={classNames(style.tabMenuItem, activeGroup === item.group && style.activeTabMenuItem)}
                        dataType={item.group}
                    >
                        <Box paddingRight={"Sm"} paddingLeft={"Sm"} gap={"Xxs"}>
                            {item.icon}
                            {t(item.label)}
                        </Box>
                    </TabMenuItem>
                ))}
            </TabMenu>

            <Box paddingTop={"Lg"} flexWrap={"Wrap"} gap={"Xs"} horizontalAlignment="Center" verticalAlignment="Center">
                {visibleTags.map((tag, index) => (
                    <Button
                        key={index}
                        variant={"Secondary"}
                        onClick={() => {
                            if (isProcessingPrompt) {
                                return;
                            }
                            setIsAiPanelOpen(true);
                            setPendingPrompt(t(tag.text));
                        }}
                        className={classNames(style.promptButton, isProcessingPrompt && style.disabledPromptButton)}
                        testId={testIds.workArea.asset.customView.overview.aiBanner.promptButton}
                        dataType={`${tag.group}_TAB_PROMPT_${index + 1}`}
                    >
                        {t(tag.text)}
                        <NavArrowRight color={"--color-purple-600"} />
                    </Button>
                ))}
            </Box>
            <Box paddingTop="Md">
                <AIDisclaimer whiteColor />
            </Box>
        </Box>
    );
};
